import React, { useState, useEffect } from 'react';
import { Typography, Button, Affix, Select, Modal, message, Input, Form, Spin, Tooltip, Checkbox, ColorPicker, Row, Col, Card } from 'antd';
import { DownOutlined, PlusOutlined, RedoOutlined, SettingOutlined, UndoOutlined, CloseOutlined, InfoCircleOutlined, LockOutlined, UnlockOutlined, DeleteOutlined } from "@ant-design/icons";
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import 'react-resizable/css/styles.css';
import "react-grid-layout/css/styles.css";

import Visuals from '../Components/Visuals';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import FiltersDynamic from '../Components/FiltersDynamicComponent';

const { Title } = Typography;
const { Option } = Select;
const ResponsiveGridLayout = WidthProvider(Responsive);
const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 320 };
const cols = { lg: 18, md: 18, sm: 9, xs: 6, xxs: 3 };
const rowHeight = 5;

const Dashboard = () => {
    const [components, setComponents] = useState([]);
    const [layout, setLayout] = useState([]);
    const [initialComponents, setInitialComponents] = useState([]);
    const [initialLayout, setInitialLayout] = useState([]);
    const [selectedChartType, setSelectedChartType] = useState('calendar');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isUnsavedChangesModalVisible, setIsUnsavedChangesModalVisible] = useState(false);
    const [currentComponentId, setCurrentComponentId] = useState(null);
    const [currentComponentName, setCurrentComponentName] = useState('');
    const [componentName, setComponentName] = useState('');
    const [componentType, setComponentType] = useState('custom');
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedReport, setSelectedReport] = useState([]);
    // Removed isStatic from modal state
    const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
    const [dashboardName, setDashboardName] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const security = useSelector((state) => state.data.items[0]);
    const [assignTo, setAssignTo] = useState([]);
    const [users, setUsers] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [apps, setApps] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [appsLoading, setAppsLoading] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [selectedRoleIds, setSelectedRoleIds] = useState([]);
    const [selectedAppId, setSelectedAppId] = useState('');
    const [widgetAdded, setWidgetAdded] = useState(null);
    const [highlightLogic, setHighlightLogic] = useState('AND');
    const [highlightFilters, setHighlightFilters] = useState([]);
    const [highlightValueOperator, setHighlightValueOperator] = useState({});
    const [highlightMessage, setHighlightMessage] = useState('');
    const [multipleHighlights, setMultipleHighlights] = useState([]);
    const [currentHighlightIndex, setCurrentHighlightIndex] = useState(-1);
    const [highlightColor, setHighlightColor] = useState('#fff2e6');
    const [highlightBorderColor, setHighlightBorderColor] = useState('#ff8c00');
    const [highlightTextColor, setHighlightTextColor] = useState('#000000');
    const [highlightBold, setHighlightBold] = useState(false);
    const [highlightFields, setHighlightFields] = useState([]);
    const [fetchingFields, setFetchingFields] = useState(false);

    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth"
        });
    }, [widgetAdded]);

    useEffect(() => {
        if (currentHighlightIndex >= 0) {
            // Find the report for the selectedReport id
            const reportObj = reports?.find(r => r._id === selectedReport);
            if (reportObj && reportObj.model) {
                setFetchingFields(true);
                const apiService = new ApiService(`${BASE_URL}/mt_fields/object/${reportObj.model}`, {
                    "Content-Type": "application/json",
                });
                apiService.makeCall().then(res => {
                    // Filter fields to keep only integer, decimal, percentage, currency, rollup
                    const allowedTypes = ["integer", "decimal", "percentage", "currency", "rollup", "picklist", "string"];
                    const filteredFields = (res || []).filter(field =>
                        allowedTypes.includes(field.type) && field.name !== "recordCount"
                    );
                    // Only include fields that are in fields_to_display
                    const displayFields = reportObj.fields_to_display || [];
                    console.log('filteredFields for highlights:', filteredFields, displayFields);

                    setHighlightFields(filteredFields.filter(f => displayFields.includes(f.name)));
                }).catch(() => {
                    setHighlightFields([]);
                }).finally(() => {
                    setFetchingFields(false);
                });
            } else {
                setHighlightFields([]);
            }
        }
    }, [currentHighlightIndex, selectedReport, reports]);

    // Fetch roles
    const fetchRoles = async () => {
        setRolesLoading(true);
        try {
            const apiServiceForRoles = new ApiService(
                `${BASE_URL}/get_all_user_role`,
                { 'Content-Type': 'application/json' },
                'GET'
            );
            const rolesData = await apiServiceForRoles.makeCall();
            setUserRoles(rolesData);
        } catch (error) {
            // Optionally handle error
        } finally {
            setRolesLoading(false);
        }
    };

    // Fetch users
    const fetchUsers = async () => {
        setUsersLoading(true);
        const apiServiceForLookup = new ApiService(
            `${BASE_URL}/fetch_records/User`,
            { 'Content-Type': 'application/json' },
            'GET'
        );
        try {
            const response = await apiServiceForLookup.makeCall();
            setUsers(response?.records || []);
        } catch (error) {
            const errorMessage = error && typeof error === 'object'
                ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
                : 'Failed to fetch users due to an unknown error';
            message.error(errorMessage);
        } finally {
            setUsersLoading(false);
        }
    };

    // Fetch apps
    const fetchApps = async () => {
        setAppsLoading(true);
        try {
            const apiService = new ApiService(`${BASE_URL}/mt_apps`, { 'Content-Type': 'application/json' }, 'GET');
            const res = await apiService.makeCall();
            console.log('Apps fetched:', res);

            const appsData = Array.isArray(res) ? res : [];
            setApps(appsData);
        } catch (error) {
            const errorMessage = error && typeof error === 'object'
                ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
                : 'Failed to fetch apps due to an unknown error';
            message.error(errorMessage);
        } finally {
            setAppsLoading(false);
        }
    };

    // Fetch users/roles when assignTo changes, always fetch apps
    useEffect(() => {
        if (assignTo?.includes('user')) {
            fetchUsers();
        }
        if (assignTo?.includes('role')) {
            fetchRoles();
        }
        // Reset selections when assignTo changes
        if (!assignTo?.includes('user')) setSelectedUserIds([]);
        if (!assignTo?.includes('role')) setSelectedRoleIds([]);
    }, [assignTo]);

    // Always fetch apps when component loads
    useEffect(() => {
        fetchApps();
    }, []);

    useEffect(() => {
        if (id) {
            fetchDashboardData(id);
        } else {
            setComponents([]);
            setLayout([]);
            setInitialComponents([]);
            setInitialLayout([]);
        }
        fetchReports();
    }, [id, security]);

    const saveHistory = (action) => {

        setUndoStack([...undoStack, { components: [...components], layout: [...layout], action }]);
        setRedoStack([]);
    };

    const undo = () => {

        if (undoStack.length > 0) {
            const lastState = undoStack[undoStack.length - 1];
            setUndoStack(undoStack.slice(0, -1));
            setRedoStack([...redoStack, { components: [...components], layout: [...layout], action: lastState.action }]);
            setComponents(lastState.components);
            setLayout(lastState.layout);
            // if (lastState.action === 'configure') {
            //     openModal(lastState.currentComponentId, lastState.currentComponentName);
            // }
        }
    };

    const redo = () => {
        if (redoStack.length > 0) {
            const nextState = redoStack[redoStack.length - 1];
            setRedoStack(redoStack.slice(0, -1));
            setUndoStack([...undoStack, { components: [...components], layout: [...layout], action: nextState.action }]);
            setComponents(nextState.components);
            setLayout(nextState.layout);
            // if (nextState.action === 'configure') {
            //     openModal(nextState.currentComponentId, nextState.currentComponentName);
            // }
        }
    };

    const fetchDashboardData = async (dashboardId) => {
        if (security?.system_settings?.dashboard.edit === false) {
            message.error('You dont have permission to update dashboard');
            return;
        }
        try {
            const apiService = new ApiService(`${BASE_URL}/get_dashboard/${dashboardId}`, {
                'Content-Type': 'application/json',
            }, 'GET');
            const res = await apiService.makeCall(navigate);

            const mappedComponents = res?.components?.map((item) => ({
                ...item.info,
            }));

            // Add 3 to the height of metric components in the layout (ensure integer value)
            const mappedLayout = res?.dashboard_layout?.map((item) => {
                let newItem = { ...item.position, i: item.position.i.toString(), static: false };
                const comp = mappedComponents?.find(c => c.id?.toString() === newItem.i);
                if (comp && comp.type === 'metric') {
                    // rowHeight is 5px, so 3 rows = 15px, ensure integer
                    newItem.h = Math.round((newItem.h || 0) + 3);
                }
                return newItem;
            });

            setComponents(mappedComponents);
            setLayout(mappedLayout);
            setInitialComponents(mappedComponents);
            setInitialLayout(mappedLayout);
            setDashboardName(res?.name);
            console.log('Dashboard data fetched successfully:', res?.name);
            setIsDefault(res?.isDefault);
            setAssignTo(res?.defaultScope || []);
            
            // Extract appId from defaultTargetIds (it's always the last item when we save)
            const targetIds = res?.defaultTargetIds || [];
            console.log('Loading targetIds:', targetIds);
            
            if (targetIds.length > 0) {
                const appId = targetIds[targetIds.length - 1];
                setSelectedAppId(appId);
                console.log('Extracted appId:', appId);
                
                // Filter out appId from user/role selections
                if (res?.defaultScope === 'user' && targetIds.length > 1) {
                    setSelectedUserIds(targetIds.slice(0, -1)); // All except last item (appId)
                } else if (res?.defaultScope === 'role' && targetIds.length > 1) {
                    setSelectedRoleIds(targetIds.slice(0, -1)); // All except last item (appId)
                }
            } else {
                // Handle case where targetIds is empty
                setSelectedAppId('');
                console.log('No targetIds found, setting appId to empty');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    const handleChartTypeChange = (value) => {
        setSelectedChartType(value);
    };

    const addWidget = () => {
        const newWidgetId = `${Date.now()}`;
        const newWidget = {
            id: newWidgetId,
            content: ``,
            type: null,
        };
        const newLayoutItem = {
            i: newWidgetId,
            x: 0,
            y: Infinity,
            w: 9,
            h: 42,
            static: false,
        };
        const updatedComponents = Array.isArray(components) ? components : [];
        const updatedLayout = Array.isArray(layout) ? layout : [];

        saveHistory('add');
        setComponents([...updatedComponents, newWidget]);
        setLayout([...updatedLayout, newLayoutItem]);
        setWidgetAdded(newWidgetId);
    };

    const deleteWidget = (id, event) => {
        event.stopPropagation();
        saveHistory('delete');
        setComponents(components?.filter(component => component.id !== id));
        setLayout(layout?.filter(item => item.i !== id));
    };

    const openModal = (id, name) => {
        const component = components?.find(component => component.id === id);
        setCurrentComponentId(id);
        setCurrentComponentName(name);
        setComponentName(component?.name || '');
        setComponentType(component?.componentType || 'custom');
        setSelectedReport(component?.reportId || '');
        setSelectedChartType(component?.type || '');
        setHighlightFilters(
            component?.highlightFilters
                ? Object.values(component?.highlightFilters).map(f => {
                    if (f && typeof f.value === 'object' && f.value !== null) {
                        const operator = Object.keys(f.value)[0];
                        const val = f.value[operator] !== undefined && f.value[operator] !== null ? String(f.value[operator]) : '';
                        return {
                            field: f.field,
                            value: val,
                            displayValue: val,
                            operator: operator,
                        };
                    } else if (f) {
                        const val = f.value !== undefined && f.value !== null ? String(f.value) : '';
                        return {
                            field: f.field,
                            value: val,
                            displayValue: val,
                            operator: 'equal',
                        };
                    } else {
                        return { field: '', value: '', displayValue: '', operator: 'equal' };
                    }
                })
                : []
        );
        setHighlightLogic(component.filterLogic || 'AND');
        setHighlightValueOperator(component.highlightValueOperator || {});
        setHighlightMessage(component.highlightMessage || '');
        setHighlightColor(component.highlightColor || '#fff2e6');
        setHighlightBorderColor(component.highlightBorderColor || '#ff8c00');
        setHighlightTextColor(component.highlightTextColor || '#000000');
        setHighlightBold(component.highlightBold || false);
        setMultipleHighlights(component.multipleHighlights || []);
        setCurrentHighlightIndex(-1);
        setIsModalVisible(true);
    };

    const handleOk = () => {
        saveHistory('configure');
        updateCurrentHighlight();
        setComponents(components?.map(component =>
            component.id === currentComponentId ? {
                ...component,
                type: selectedChartType,
                name: componentName,
                componentType: componentType,
                reportId: componentType === 'report' ? selectedReport : null,
                reportName: componentType === 'report' ? reports?.find(report => report._id === selectedReport)?.name : '',
                multipleHighlights: multipleHighlights,
            } : component
        ));

        setIsModalVisible(false);
        setComponentName('');
        setComponentType('custom');
        setSelectedReport('');
        setSelectedChartType('');
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setComponentName('');
        setComponentType('custom');
        setSelectedReport('');
        setSelectedChartType('');
    };
    // Toggle static property for a component (lock/unlock position)
    const toggleStatic = (id) => {
        setLayout(prevLayout => prevLayout.map(item =>
            item.i === id.toString() ? { ...item, static: !item.static } : item
        ));
    };

    const handleBack = () => {
        if (JSON.stringify(layout) !== JSON.stringify(initialLayout) || JSON.stringify(components) !== JSON.stringify(initialComponents)) {
            setIsUnsavedChangesModalVisible(true);
        } else {
            navigate(-1); // Go back to the previous page
        }
    };

    const handleSave = async () => {
        console.log('Saving dashboard with components:', components);
        console.log('Selected App ID:', selectedAppId);
        console.log('Assign To:', assignTo);
        console.log('Selected User IDs:', selectedUserIds);
        console.log('Selected Role IDs:', selectedRoleIds);
        
        // Validate required app selection
        if (!selectedAppId || selectedAppId.trim() === '') {
            message.error('Please select an app before saving the dashboard');
            return;
        }

        let defaultScope = '';
        let defaultTargetIds = [];
        if (assignTo?.includes('user') && selectedUserIds.length > 0) {
            defaultScope = 'user';
            defaultTargetIds = [...selectedUserIds, selectedAppId]; // Include appId in targetIds
        } else if (assignTo?.includes('role') && selectedRoleIds.length > 0) {
            defaultScope = 'role';
            defaultTargetIds = [...selectedRoleIds, selectedAppId]; // Include appId in targetIds
        } else if (assignTo?.includes('org')) {
            defaultScope = 'org';
            defaultTargetIds = [selectedAppId]; // Include appId in targetIds
        } else {
            // If no specific assignment is made, default to org-wide with appId
            defaultScope = 'org';
            defaultTargetIds = [selectedAppId];
        }

        console.log('Final defaultScope:', defaultScope);
        console.log('Final defaultTargetIds:', defaultTargetIds);

        // Deduct 3 from the height of metric components before saving (ensure integer)
        const adjustedLayout = layout?.map(item => {
            // Find the corresponding component
            const comp = components?.find(c => c.id?.toString() === item.i?.toString());
            if (comp && comp.type === 'metric') {
                // rowHeight is 5px, so deduct 3 rows
                const newH = Math.max(1, Math.round(item.h - 3)); // Prevent h from going below 1
                return { ...item, h: newH };
            }
            return item;
        });

        const dashboardData = {
            name: dashboardName || "New Dash",
            dashboard_layout: adjustedLayout?.map(item => ({ position: item })),
            components: components?.map(item => ({
                info: item,
                name: item.name,
                type: item.type,
                componentType: item.componentType,
                reports: item.reports,
            })),
            isDefault: isDefault,
            assignTo: assignTo,
            defaultScope,
            defaultTargetIds, // appId is now included in this array
        };

        const payload = {
            mt_dashboard: dashboardData
        };
        console.log('Payload to save:', payload);


        setUndoStack([]);
        setRedoStack([]);

        try {
            const apiService = new ApiService(`${BASE_URL}/${id ? `update_dashboard/${id}` : 'create_dashboard'}`, {
                'Content-Type': 'application/json',
            }, id ? 'PATCH' : 'POST', JSON.stringify(payload));
            await apiService.makeCall(navigate);
            message.success('Dashboard saved successfully!');
            setInitialComponents(components);
            setInitialLayout(layout);
            setIsUnsavedChangesModalVisible(false);
            setIsSettingsModalVisible(false);
            navigate(-1);
        } catch (error) {
            console.error('Error saving dashboard:', error);
            message.error('Error saving dashboard!');
        }
    };

    const handleDiscardChanges = () => {
        setIsUnsavedChangesModalVisible(false);
        navigate(-1);
    };

    const handleComponentNameChange = (e) => {
        setComponentName(e.target.value);
    };

    const handleComponentTypeChange = (value) => {
        setComponentType(value);
    };

    const handleReportChange = (value) => {
        setSelectedReport(value);
    };

    const fetchReports = async () => {
        try {
            const apiService = new ApiService(`${BASE_URL}/mt_reports`, {
                'Content-Type': 'application/json',
            }, 'GET');
            const res = await apiService.makeCall(navigate);
            setReports(res);
            setLoading(false);
        } catch (err) {
            const errorMessage = err && typeof err === 'object'
                ? Object.entries(err).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
                : 'Error fwtching reports';
            message.error(errorMessage);
        }
    };

    const openSettingsModal = () => {
        setIsSettingsModalVisible(true);
    };

    const handleSettingsCancel = () => {
        setIsSettingsModalVisible(false);
    };

    const handleMetricsChange = (updatedMetrics) => {
        console.log('Updated Metrics in dashbaord save:', updatedMetrics);
        setComponents(prev =>
            prev.map(comp =>
                comp.type === "metric"
                    ? {
                        ...comp,
                        metrics: updatedMetrics
                    }
                    : comp
            )
        );
    };

    // Helper functions for managing multiple highlights
    const addNewHighlight = () => {
        const newHighlight = {
            id: Date.now(),
            name: `Highlight ${multipleHighlights.length + 1}`,
            filters: {},
            filterLogic: 'AND',
            message: '',
            backgroundColor: '#fff2e6',
            borderColor: '#ff8c00',
            textColor: '#000000',
            bold: false
        };

        const updatedHighlights = [...multipleHighlights, newHighlight];
        setMultipleHighlights(updatedHighlights);
        setCurrentHighlightIndex(updatedHighlights.length - 1);

        // Reset current form fields to new highlight values
        setHighlightFilters([]);
        setHighlightLogic('AND');
        setHighlightMessage('');
        setHighlightColor('#fff2e6');
        setHighlightBorderColor('#ff8c00');
        setHighlightTextColor('#000000');
        setHighlightBold(false);
        setHighlightValueOperator({});
    };

    const selectHighlight = (index) => {
        if (index >= 0 && index < multipleHighlights.length) {
            const highlight = multipleHighlights[index];
            setCurrentHighlightIndex(index);

            // Load highlight data into form fields
            const filters = highlight.filters ? Object.values(highlight.filters).map(f => {
                if (f && typeof f.value === 'object' && f.value !== null) {
                    const operator = Object.keys(f.value)[0];
                    const val = f.value[operator] !== undefined && f.value[operator] !== null ? String(f.value[operator]) : '';
                    return {
                        field: f.field,
                        value: val,
                        displayValue: val,
                        operator: operator,
                    };
                } else if (f) {
                    const val = f.value !== undefined && f.value !== null ? String(f.value) : '';
                    return {
                        field: f.field,
                        value: val,
                        displayValue: val,
                        operator: 'equal',
                    };
                } else {
                    return { field: '', value: '', displayValue: '', operator: 'equal' };
                }
            }) : [];

            setHighlightFilters(filters);
            setHighlightLogic(highlight.filterLogic || 'AND');
            setHighlightMessage(highlight.message || '');
            setHighlightColor(highlight.backgroundColor || '#fff2e6');
            setHighlightBorderColor(highlight.borderColor || '#ff8c00');
            setHighlightTextColor(highlight.textColor || '#000000');
            setHighlightBold(highlight.bold || false);
            setHighlightValueOperator({});
        }
    };

    const updateCurrentHighlight = () => {
        console.log('Updating highlight at index:', currentHighlightIndex);
        console.log('Current highlightFilters:', highlightFilters);
        console.log('Current highlightLogic:', highlightLogic);
        console.log('Current highlightMessage:', highlightMessage);
        console.log('Current highlightColor:', highlightColor);

        // ...existing code...


        if (currentHighlightIndex >= 0 && currentHighlightIndex < multipleHighlights.length) {
            const filterObj = highlightFilters.reduce((acc, filter, index) => {
                if (filter.field) {
                    acc[index + 1] = {
                        field: filter.field,
                        value: filter.operator && (filter.value === undefined || filter.value === "")
                            ? { [filter.operator]: "" }
                            : (filter.value === undefined ? "" : filter.value)
                    };
                }

                //   setMultipleHighlights(updatedHighlights);
                return acc;
            }, {});

            let filterLogic = '';
            const filterObjKeys = Object.keys(filterObj);
            if (filterObjKeys.length === 1) {
                filterLogic = '1';
            } else if (filterObjKeys.length > 1) {
                filterLogic = highlightLogic || '';
            }

            const updatedHighlights = [...multipleHighlights];
            updatedHighlights[currentHighlightIndex] = {
                ...updatedHighlights[currentHighlightIndex],
                filters: filterObj,
                filterLogic: filterLogic,
                message: highlightMessage,
                backgroundColor: highlightColor,
                borderColor: highlightBorderColor,
                textColor: highlightTextColor,
                bold: highlightBold
            };

            setMultipleHighlights(updatedHighlights);
        }
    };

    const deleteHighlight = (index) => {
        const updatedHighlights = multipleHighlights.filter((_, i) => i !== index);
        setMultipleHighlights(updatedHighlights);

        if (currentHighlightIndex === index) {
            setCurrentHighlightIndex(-1);
            // Reset form fields
            setHighlightFilters([]);
            setHighlightLogic('AND');
            setHighlightMessage('');
            setHighlightColor('#fff2e6');
            setHighlightBorderColor('#ff8c00');
            setHighlightTextColor('#000000');
            setHighlightBold(false);
            setHighlightValueOperator({});
        } else if (currentHighlightIndex > index) {
            setCurrentHighlightIndex(currentHighlightIndex - 1);
        }
    };

    const getHighlightRowStyle = (record, component) => {
        if (!component?.multipleHighlights || !record || !Array.isArray(component?.multipleHighlights)) {
            return {};
        }

        // Check each highlight rule and return the first matching one
        for (const highlight of component?.multipleHighlights) {
            if (!highlight?.filters || Object.keys(highlight?.filters).length === 0) {
                continue;
            }

            const filters = Object.values(highlight?.filters);
            const logic = highlight?.filterLogic || 'AND';

            // Check if record matches highlight conditions
            const matchResults = filters.map(filter => {
                if (!filter.field || filter.field === '') return false;

                const recordValue = record[filter.field];
                const filterValue = typeof filter.value === 'object'
                    ? Object.values(filter.value)[0]
                    : filter.value;

                if (typeof filter.value === 'object' && filter.value !== null) {
                    const operator = Object.keys(filter.value)[0];
                    switch (operator) {
                        case 'equal':
                            return recordValue === filterValue;
                        case 'not_equal':
                            return recordValue !== filterValue;
                        case 'contains':
                            return recordValue && recordValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
                        case 'not_contains':
                            return !recordValue || !recordValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
                        case 'starts_with':
                            return recordValue && recordValue.toString().toLowerCase().startsWith(filterValue.toString().toLowerCase());
                        case 'ends_with':
                            return recordValue && recordValue.toString().toLowerCase().endsWith(filterValue.toString().toLowerCase());
                        case 'greater_than':
                            return Number(recordValue) > Number(filterValue);
                        case 'less_than':
                            return Number(recordValue) < Number(filterValue);
                        case 'greater_equal':
                            return Number(recordValue) >= Number(filterValue);
                        case 'less_equal':
                            return Number(recordValue) <= Number(filterValue);
                        default:
                            return recordValue === filterValue;
                    }
                } else {
                    return recordValue === filterValue;
                }
            });

            // Apply logic (AND/OR)
            let matches = false;
            if (logic === 'AND') {
                matches = matchResults.length > 0 && matchResults.every(result => result === true);
            } else if (logic === 'OR') {
                matches = matchResults.some(result => result === true);
            } else if (logic === '1' && matchResults.length === 1) {
                matches = matchResults[0];
            }

            // Return highlight style if matches (first match wins)
            if (matches) {
                return {
                    style: {
                        backgroundColor: highlight?.backgroundColor,
                        border: `1px solid ${highlight?.borderColor}`,
                        color: highlight?.textColor,
                        fontWeight: highlight?.bold ? 'bold' : 'normal'
                    },
                    message: highlight?.message || null
                };
            }
        }
        return { style: {}, message: null };
    };

    return (
        <>
            <Affix offsetTop={64}>
                <div style={styles.header}>
                    <Title level={5} style={styles.headerTitle}>{id ? 'Edit ' + dashboardName : 'Create Dashboard'}</Title>
                    <div style={styles.headerButtons}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={addWidget}>Widget</Button>
                        <Button type="primary" onClick={undo} disabled={undoStack.length === 0} icon={<UndoOutlined />}></Button>
                        <Button type="primary" onClick={redo} disabled={redoStack.length === 0} icon={<RedoOutlined />}></Button>
                        <Button type="primary" icon={<SettingOutlined />} onClick={openSettingsModal}></Button>
                        <Button type="primary" onClick={handleSave}>Save</Button>
                        <div style={styles.done} onClick={handleBack}>Done</div>
                    </div>
                </div>
            </Affix>
            <div style={styles.parentContainer}>
                <div style={styles.cardsContainer}>
                    <ResponsiveGridLayout
                        className="my-1 mx-1"
                        breakpoints={breakpoints}
                        cols={cols}
                        layouts={{ lg: layout }}
                        rowHeight={rowHeight}
                        onLayoutChange={(curLayout, allLayouts) => {
                            setLayout(curLayout);      // keep lg layout
                            // optional: if you want multiple breakpoints, also store allLayouts
                        }}
                        draggableCancel=".non-draggable"
                        resizeHandles={['se', 'sw', 'ne', 'nw']}
                    >
                        {components?.map((component) => {
                            const layoutItem = layout?.find((item) => item.i === component.id.toString());
                            return (
                                <div
                                    className="grid-cell"
                                    key={component?.id}
                                    data-grid={{ ...layoutItem, resizeHandles: ['se', 'sw', 'ne', 'nw'] }}
                                    style={{
                                        ...(component?.type === "metric" ? styles.metricItem : { ...styles.gridItem, padding: "8px" }),
                                        overflow: 'hidden',
                                        minHeight: 120,
                                        minWidth: 180,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <div style={{ ...styles.gridHeader, padding: "2px" }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <h2 style={{ margin: 0 }}>{component?.name || component?.content}</h2>
                                            {component?.reportId && (
                                                <Tooltip title={`View Full Report`}>
                                                    <a
                                                        href={`/ViewReport/${component?.reportId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#209aff', marginLeft: 2, fontSize: 11 }}
                                                        title="View Full Report"
                                                    >
                                                        <InfoCircleOutlined />
                                                    </a>
                                                </Tooltip>
                                            )}
                                        </div>
                                        <div style={styles.iconContainer}>
                                            {/* Lock/Unlock icon for static property */}
                                            <Tooltip title={layoutItem?.static ? 'Unlock position' : 'Lock position'}>
                                                {layoutItem?.static ? (
                                                    <LockOutlined
                                                        className="non-draggable"
                                                        style={{ ...styles.icon, color: '#fa4614ff' }}
                                                        onClick={() => toggleStatic(component.id)}
                                                    />
                                                ) : (
                                                    <UnlockOutlined
                                                        className="non-draggable"
                                                        style={{ ...styles.icon, color: '#20ff18ff' }}
                                                        onClick={() => toggleStatic(component.id)}
                                                    />
                                                )}
                                            </Tooltip>
                                            <SettingOutlined
                                                className="non-draggable"
                                                style={styles.icon}
                                                onClick={() => openModal(component.id, component.content)}
                                            />
                                            <CloseOutlined
                                                className="non-draggable"
                                                style={styles.icon}
                                                onClick={(event) => deleteWidget(component.id, event)}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        {component.type ? (
                                            <div style={{ width: '100%', height: '100%', minHeight: 80, minWidth: 120, boxSizing: 'border-box', overflow: 'hidden' }}>
                                                <Visuals component={component} type={component.type} reportId={component.reportId} onMetricsChange={handleMetricsChange} isEditingWindow={true} getHighlightRowStyle={getHighlightRowStyle} />
                                            </div>
                                        ) : (
                                            <div style={styles.placeholder} className="non-draggable">
                                                Please configure your widget by clicking on the configure icon.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </ResponsiveGridLayout>
                </div>
            </div>

            {/* Modal for Configuration */}
            <Modal
                title={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            minHeight: "20px", // ensures title area has height
                        }}
                    >
                        {`Configure ${currentComponentName}`}
                    </div>
                }
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                zIndex={1002}
                okText="Save"
                cancelText="Cancel"
                width="80vw"
                className="non-draggable centered-modal"
                bodyStyle={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    padding: "24px",
                }}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                }}
                footer={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "16px",
                            padding: "6px 0",
                        }}
                    >
                        <Button
                            onClick={handleCancel}
                            style={{
                                border: "1px solid #d9d9d9",
                                borderRadius: "6px",
                                padding: "6px 16px",
                                background: "white",
                                cursor: "pointer",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleOk}
                            type="primary"
                            style={{
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 16px",
                                color: "white",
                                cursor: "pointer",
                            }}
                        >
                            Save
                        </Button>
                    </div>
                }

            >

                <Card
                    style={{
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                >

                    <Form layout="vertical">
                        <Row gutter={[24, 16]}>
                            {/* Left column */}
                            <Col span={12}>
                                <Form.Item label="Name">
                                    <Input
                                        placeholder="Enter component name"
                                        value={componentName}
                                        onChange={handleComponentNameChange}
                                        style={{ marginBottom: '6px' }}
                                    />
                                </Form.Item>
                                <Form.Item label="Type">
                                    <Select
                                        style={{ width: '100%', marginBottom: '6px' }}
                                        onChange={handleComponentTypeChange}
                                        value={componentType}
                                        placeholder="Select component type"
                                    >
                                        <Option value="custom">Custom</Option>
                                        <Option value="report">Report</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            {componentType === 'report' ? (
                                <>
                                    <Col span={12}>

                                        <Form.Item label="Chart Type">
                                            <Select
                                                placeholder="Select chart type"
                                                style={{ width: '100%' }}
                                                onChange={handleChartTypeChange}
                                                value={selectedChartType}
                                            >
                                                <Option value="bar">Bar</Option>
                                                <Option value="radar">Radar</Option>
                                                <Option value="line">Line</Option>
                                                <Option value="pie">Pie</Option>
                                                <Option value="funnel">Funnel</Option>
                                                <Option value="metric">Metrics Widget</Option>
                                                <Option value="table">Table</Option>
                                                <Option value="logBook">Log Book</Option>
                                                <Option value="heatmap">Heatmap</Option>
                                            </Select>
                                        </Form.Item>
                                        <Form.Item label="Report">
                                            <Select
                                                style={{ width: '100%' }}
                                                placeholder="Select report"
                                                suffixIcon={loading ? <Spin size="small" /> : null}
                                                disabled={loading || !selectedChartType || selectedChartType === 'metric' || selectedChartType === 'logBook'}
                                                onChange={handleReportChange}
                                                value={selectedReport}
                                            >
                                                {reports?.map((report) => (
                                                    <Option key={report._id} value={report._id}>{report.name}</Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    {selectedChartType !== 'logBook' && selectedChartType !== 'metric' && (
                                        <>
                                            <Form.Item label="Row Highlights">
                                                <div style={{ marginBottom: '16px' }}>
                                                    <Button
                                                        type="dashed"
                                                        onClick={addNewHighlight}
                                                        style={{ width: '100%', marginBottom: '12px' }}
                                                        icon={<PlusOutlined />}
                                                    >
                                                        Add New Highlight Rule
                                                    </Button>

                                                    {multipleHighlights.length > 0 && (
                                                        <div style={{
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            border: '1px solid #d9d9d9',
                                                            borderRadius: '6px',
                                                            padding: '8px'
                                                        }}>
                                                            {multipleHighlights.map((highlight, index) => (
                                                                <div
                                                                    key={highlight.id}
                                                                    style={{
                                                                        padding: '8px',
                                                                        marginBottom: '8px',
                                                                        border: `2px solid ${currentHighlightIndex === index ? '#1890ff' : '#f0f0f0'}`,
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        backgroundColor: currentHighlightIndex === index ? '#f6ffed' : '#fafafa',
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center'
                                                                    }}
                                                                    onClick={() => selectHighlight(index)}
                                                                >
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <div
                                                                            style={{
                                                                                width: '16px',
                                                                                height: '16px',
                                                                                backgroundColor: highlight.backgroundColor,
                                                                                border: `1px solid ${highlight.borderColor}`,
                                                                                borderRadius: '3px'
                                                                            }}
                                                                        />
                                                                        <span style={{ fontWeight: currentHighlightIndex === index ? 'bold' : 'normal' }}>
                                                                            {highlight.name}
                                                                        </span>
                                                                        {highlight.message && (
                                                                            <span style={{ fontSize: '12px', color: '#666' }}>
                                                                                - {highlight.message}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<DeleteOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            deleteHighlight(index);
                                                                        }}
                                                                        style={{ color: '#ff4d4f' }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {currentHighlightIndex >= 0 && (
                                                    <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px' }}>
                                                        <div style={{ marginBottom: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                                            Editing: {multipleHighlights[currentHighlightIndex]?.name}
                                                        </div>

                                                        <Form.Item label="Highlight Name" style={{ marginBottom: '12px' }}>
                                                            <Input
                                                                placeholder="Enter highlight name"
                                                                value={multipleHighlights[currentHighlightIndex]?.name || ''}
                                                                onChange={(e) => {
                                                                    const updatedHighlights = [...multipleHighlights];
                                                                    updatedHighlights[currentHighlightIndex].name = e.target.value;
                                                                    setMultipleHighlights(updatedHighlights);
                                                                }}
                                                            />
                                                        </Form.Item>
                                                    </div>
                                                )}
                                            </Form.Item>

                                            {currentHighlightIndex >= 0 && (
                                                <Form.Item label="Highlight Filters">
                                                    <FiltersDynamic
                                                        fields={highlightFields}
                                                        logic={highlightLogic}
                                                        setLogic={setHighlightLogic}
                                                        filters={highlightFilters}
                                                        setFilters={setHighlightFilters}
                                                        setValueOperator={setHighlightValueOperator}
                                                        valueOperator={highlightValueOperator}
                                                        fetchLookupRecordsForExistingFilters={() => { }}
                                                        fetchLookupRecords={() => { }}
                                                        lookupOptions={{}}
                                                        heading={false}
                                                    />
                                                </Form.Item>
                                            )}
                                            {selectedChartType !== 'logBook' && selectedChartType !== 'metric' && currentHighlightIndex >= 0 && (
                                                <Form.Item label="Highlight Message">
                                                    <Input
                                                        placeholder="Enter highlight message"
                                                        value={highlightMessage || ''}
                                                        onChange={(e) => setHighlightMessage(e.target.value)}
                                                        style={{ marginBottom: '6px' }}
                                                    />
                                                </Form.Item>
                                            )}
                                            {selectedChartType !== 'logBook' && selectedChartType !== 'metric' && currentHighlightIndex >= 0 && (
                                                <Form.Item label="Highlight Colors">
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ minWidth: '120px' }}>Background Color:</span>
                                                            <ColorPicker
                                                                value={highlightColor}
                                                                onChange={(color) => setHighlightColor(color.toHexString())}
                                                                showText
                                                                size="small"
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ minWidth: '120px' }}>Border Color:</span>
                                                            <ColorPicker
                                                                value={highlightBorderColor}
                                                                onChange={(color) => setHighlightBorderColor(color.toHexString())}
                                                                showText
                                                                size="small"
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ minWidth: '120px' }}>Text Color:</span>
                                                            <ColorPicker
                                                                value={highlightTextColor}
                                                                onChange={(color) => setHighlightTextColor(color.toHexString())}
                                                                showText
                                                                size="small"
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ minWidth: '120px' }}>Bold Text:</span>
                                                            <Checkbox
                                                                checked={highlightBold}
                                                                onChange={(e) => setHighlightBold(e.target.checked)}
                                                            />
                                                        </div>
                                                        <div style={{ marginTop: '12px' }}>
                                                            <Button
                                                                type="primary"
                                                                size="small"
                                                                onClick={updateCurrentHighlight}
                                                                style={{ width: '100%' }}
                                                            >
                                                                Update Highlight Rule
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Form.Item>
                                            )}
                                        </>
                                    )}

                                </>
                            ) : (
                                <Form.Item label="Custom Component">
                                    <Select
                                        placeholder="Select custom component"
                                        style={{ width: '100%' }}
                                        onChange={handleChartTypeChange}
                                        value={selectedChartType}
                                    >
                                        <Option value="approval">Approvals</Option>
                                        <Option value="myTasks">Tasks</Option>
                                        {/* <Option value="dailyOpeningClosingStock">Daily Opening & Closing Stock</Option> */}
                                    </Select>
                                </Form.Item>
                            )}
                        </Row>
                    </Form>
                </Card>
            </Modal>
            {/* Modal for Unsaved Changes */}
            <Modal
                title="Unsaved Changes"
                visible={isUnsavedChangesModalVisible}
                zIndex={1002}   // higher than sidebar
                bodyStyle={{
                    maxHeight: '60vh', // or a fixed px value like '500px'
                    overflowY: 'auto',
                    padding: '24px',
                }}
                onCancel={() => setIsUnsavedChangesModalVisible(false)}
                footer={[
                    <Button key="discard" onClick={handleDiscardChanges}>
                        Discard
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSave}>
                        Save
                    </Button>,
                ]}
            >
                <p>Do you want to keep your changes?</p>
            </Modal>
            {/* Modal for Settings */}
            <Modal
                title={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            minHeight: "20px", // ensures title area has height
                        }}
                    >
                        Dashboard Settings
                    </div>
                }
                visible={isSettingsModalVisible}
                onOk={handleSave}
                zIndex={1002}   // higher than sidebar
                onCancel={handleSettingsCancel}
                okText="Save"
                cancelText="Cancel"
                width="80vw"
                className="non-draggable centered-modal"
                bodyStyle={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    padding: "24px",
                }}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                }}
                footer={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "16px",
                            padding: "6px 0",
                        }}
                    >
                        <Button
                            onClick={handleSettingsCancel}
                            style={{
                                border: "1px solid #d9d9d9",
                                borderRadius: "6px",
                                padding: "6px 16px",
                                background: "white",
                                cursor: "pointer",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            type="primary"
                            style={{
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 16px",
                                color: "white",
                                cursor: "pointer",
                            }}
                        >
                            Save
                        </Button>
                    </div>
                }


            >
                <Card
                    style={{
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                >
                    <Form layout="vertical">
                        <Row gutter={[24, 16]}>
                            {/* Left column */}
                            <Col span={12}>
                                <Form.Item label="Dashboard Name">
                                    <Input
                                        placeholder="Enter dashboard name"
                                        value={dashboardName}
                                        onChange={(e) => setDashboardName(e.target.value)}
                                        style={{ marginBottom: '6px' }}
                                    />
                                </Form.Item>
                                <Form.Item label="Set as Default">
                                    <Checkbox
                                        style={{ marginBottom: '6px' }}
                                        onChange={(e) => setIsDefault(e.target.checked)}
                                        checked={isDefault}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="App" required>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Select an app (Required)"
                                        allowClear
                                        loading={appsLoading}
                                        value={selectedAppId}
                                        onChange={setSelectedAppId}
                                    >
                                        {apps && apps.length > 0 ? (
                                            apps.map((app) => (
                                                <Option key={app._id} value={app._id}>
                                                    {app.label}
                                                </Option>
                                            ))
                                        ) : (
                                            <Option disabled>No apps found</Option>
                                        )}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Assign to">
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Select assignment scope"
                                        mode="single"
                                        allowClear
                                        value={assignTo}
                                        onChange={setAssignTo}
                                    >
                                        <Option value="user">User</Option>
                                        <Option value="role">Role</Option>
                                        {/* <Option value="group">Group</Option> */}
                                        <Option value="org">Organization Wide</Option>
                                    </Select>
                                </Form.Item>
                                {assignTo?.includes('user') && (
                                    <Form.Item label="User">
                                        <Select
                                            style={{ width: '100%' }}
                                            placeholder="Select user"
                                            mode="multiple"
                                            allowClear
                                            loading={usersLoading}
                                            value={selectedUserIds}
                                            onChange={setSelectedUserIds}
                                        >
                                            {users && users.length > 0 ? (
                                                users.map((user) => (
                                                    <Option key={user._id} value={user._id}>
                                                        {user.Name}
                                                    </Option>
                                                ))
                                            ) : (
                                                <Option disabled>No users found</Option>
                                            )}
                                        </Select>
                                    </Form.Item>
                                )}
                                {assignTo?.includes('role') && (
                                    <Form.Item label="Role">
                                        <Select
                                            style={{ width: '100%' }}
                                            placeholder="Select role"
                                            mode="multiple"
                                            allowClear
                                            loading={rolesLoading}
                                            value={selectedRoleIds}
                                            onChange={setSelectedRoleIds}
                                        >
                                            {userRoles && userRoles.length > 0 ? (
                                                userRoles.map((role) => (
                                                    <Option key={role._id} value={role._id}>
                                                        {role.Name}
                                                    </Option>
                                                ))
                                            ) : (
                                                <Option disabled>No roles found</Option>
                                            )}
                                        </Select>
                                    </Form.Item>

                                )}
                                {/* {assignTo.includes('group') && (
                        <Form.Item label="Group">
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="Select group"
                                mode="multiple"
                                allowClear
                            >
                                <Option value="group1">Group 1</Option>
                                <Option value="group2">Group 2</Option>
                            </Select>
                        </Form.Item>
                    )} */}
                            </Col>
                        </Row>

                    </Form>
                </Card>
            </Modal>
        </>
    );
};

const styles = {
    parentContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#f0f2f5",
        minHeight: "100vh",
        height: "auto",
    },
    metricItem: {
        position: 'relative',
        backgroundColor: 'transparent',
        height: 'auto',
        border: 'none',
        boxShadow: 'none',
        padding: 0,
    },
    header: {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "4px 10px",
        backgroundColor: "#fff",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        zIndex: 1000,
    },
    headerTitle: {
        margin: 0,
    },
    headerButtons: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    cardsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        width: "100%",
        justifyContent: "center",
        flexGrow: 1,
        height: "auto",
    },
    gridItem: {
        position: 'relative',
        border: "1px solid rgb(230, 228, 228)",
        borderRadius: '22px',
        padding: '2px',
        backgroundColor: '#fff',
        height: 'auto',
    },
    gridHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '2px', // reduced padding
        paddingBottom: '6px',
        fontSize: '0.6rem',
    },
    iconContainer: {
        display: 'flex',
        gap: '10px',
        paddingRight: '12px'
    },
    icon: {
        cursor: 'pointer',
    },
    placeholder: {
        color: '#aaa',
        textAlign: 'center',
        marginTop: '10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
    done: {
        cursor: 'pointer',
        color: '#1890ff',
    },
};

export default Dashboard;